import { QueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import { ApiError } from '@/api/errors';

/**
 * Retry only failures that could plausibly resolve themselves.
 *
 * The default `retry: 3` turns every 403 into four identical rejected requests
 * and every validation failure into a four-second wait before the editor sees
 * the error. `ApiError.isRetryable` encodes the distinction.
 */
function shouldRetry(failureCount: number, error: unknown): boolean {
  if (!(error instanceof ApiError)) {
    return false;
  }

  return error.isRetryable && failureCount < 2;
}

export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        retry: shouldRetry,
        // Content changes come from other editors, not from this tab, so a short
        // window of staleness is fine and saves a refetch on every navigation.
        staleTime: 30_000,
        gcTime: 5 * 60_000,
        refetchOnWindowFocus: false,
        throwOnError: false,
      },
      mutations: {
        retry: false,
        // A failed save must always say something. Individual mutations can pass
        // their own `onError` to add context; this is the floor, not the ceiling.
        onError: (error) => {
          const message =
            error instanceof ApiError
              ? error.isValidation
                ? 'Please fix the highlighted fields.'
                : error.message
              : 'Something went wrong. Please try again.';

          toast.error(message);
        },
      },
    },
  });
}
